import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { makeStyles } from 'tss-react/mui';
import { IconButton, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';

// =============================================================================
// PhotoViewer — in-page, full-screen lightbox for a single chat image.
//
// Replaces the old "open the raw image in a new browser tab" behavior so the
// user never leaves the chat page. On touch devices it is a real gesture
// surface: pinch-zoom, one-finger pan, double-tap zoom, plus a toolbar for
// zoom / rotate / reset that covers desktop + no-touch users.
//
// Gestures are hand-rolled on native Pointer Events (no gesture library) —
// consistent with how the rest of the app hand-rolls drag (VideoGrid,
// Resizable, the react-rnd Window). The shell mirrors the existing full-screen
// overlay pattern (ReconnectModal / KickedModal): a fixed scrim above
// everything, portaled to <body> so it escapes the chat column's overflow /
// stacking context.
//
// Props:
//   open    - whether the viewer is shown (boolean)
//   src     - already-resolved absolute image URL (string) | null
//   alt     - accessible label / alt text (string)
//   onClose - dismiss handler
// =============================================================================

const MIN_SCALE = 1;
const MAX_SCALE = 6;
// Double-tap / double-click toggles between fit and this zoom level.
const DOUBLE_TAP_SCALE = 2.5;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const IDENTITY = { scale: 1, tx: 0, ty: 0, rotate: 0 };

const useStyles = makeStyles()((theme) => ({
  // Full-screen scrim. Darker than the session modals — this is a photo viewer,
  // so we want the image to pop against near-black. Sits above everything.
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.modal + 10,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Kill any default touch gestures (page scroll / pinch-to-zoom the page)
    // so our own pointer handling owns the interaction.
    touchAction: 'none',
    overflow: 'hidden',
  },
  // The image "stage" — receives the pointer/wheel events and hosts the <img>.
  // Fills the overlay so panning has room; the img is centered within.
  stage: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    touchAction: 'none',
    cursor: 'grab',
    '&:active': { cursor: 'grabbing' },
  },
  image: {
    maxWidth: '100vw',
    maxHeight: '100vh',
    // Leave a little breathing room around the fitted image.
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    objectFit: 'contain',
    userSelect: 'none',
    // The transform is applied inline; transformOrigin stays centered so pinch
    // + rotate feel natural. No CSS transition during a live gesture (would lag
    // the fingers); a short ease is applied only for button/double-tap changes
    // via the `animated` modifier below.
    transformOrigin: 'center center',
    willChange: 'transform',
    // The <img> itself ignores pointer events; the stage owns all gestures.
    // (Native drag-ghost is also suppressed via draggable={false}.)
    pointerEvents: 'none',
  },
  animated: {
    transition: 'transform 0.18s ease',
  },
  // Translucent toolbar pill, top-right. Theme-aware via custom tokens.
  toolbar: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderRadius: 999,
    backgroundColor: 'rgba(20, 24, 33, 0.72)',
    border: `1px solid ${theme.palette.custom.lineStrong}`,
    backdropFilter: 'blur(6px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
  },
  button: {
    color: theme.palette.custom.ink,
    '&:hover': { backgroundColor: theme.palette.custom.hoverOverlay },
  },
}));

const PhotoViewer = ({ open, src, alt = 'Image', onClose }) => {
  const { classes, cx } = useStyles();

  // The live transform lives in a ref so per-pointer-move mutations don't
  // re-render (same live-ref + committed-state split the window manager uses).
  // `view` mirrors it for the toolbar buttons + inline transform string.
  const tfRef = useRef({ ...IDENTITY });
  const [view, setView] = useState({ ...IDENTITY });
  // Whether the NEXT transform change should animate (button/double-tap) vs.
  // track the fingers 1:1 (live drag/pinch).
  const [animated, setAnimated] = useState(false);

  const imgRef = useRef(null);
  const closeBtnRef = useRef(null);
  // Active pointers: id -> {x, y}. Drives pan (1) vs. pinch (2).
  const pointersRef = useRef(new Map());
  // Pinch bookkeeping: initial finger distance + the scale at pinch start.
  const pinchRef = useRef(null);
  // Double-tap detection (touch): last tap time + position.
  const lastTapRef = useRef({ t: 0, x: 0, y: 0 });

  // Commit the live ref into React state (re-render). `withAnim` requests the
  // CSS ease for one paint (button / double-tap / reset).
  const commit = useCallback((withAnim = false) => {
    setAnimated(withAnim);
    setView({ ...tfRef.current });
  }, []);

  const setTransform = useCallback((next, withAnim = false) => {
    const t = tfRef.current;
    const scale = clamp(next.scale ?? t.scale, MIN_SCALE, MAX_SCALE);
    // Re-center automatically once we're back at (or below) fit scale.
    const atFit = scale <= MIN_SCALE + 0.001;
    tfRef.current = {
      scale,
      tx: atFit ? 0 : next.tx ?? t.tx,
      ty: atFit ? 0 : next.ty ?? t.ty,
      rotate: next.rotate ?? t.rotate,
    };
    commit(withAnim);
  }, [commit]);

  const reset = useCallback(() => {
    tfRef.current = { ...IDENTITY };
    commit(true);
  }, [commit]);

  // --- Reset whenever we open or the source image changes -------------------
  useEffect(() => {
    if (open) {
      tfRef.current = { ...IDENTITY };
      setView({ ...IDENTITY });
      setAnimated(false);
      pointersRef.current.clear();
      pinchRef.current = null;
    }
  }, [open, src]);

  // --- Body scroll lock while open ------------------------------------------
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // --- Esc to close + focus the close button on open ------------------------
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    // Focus the close button so Tab/Enter/Esc land in the dialog, not chat.
    const id = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(id);
    };
  }, [open, onClose]);

  // --- Wheel zoom (desktop), around the cursor ------------------------------
  // Bound as a NON-passive native listener so preventDefault() actually stops
  // the page from scrolling/zooming (React's onWheel is passive by default).
  const stageRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const t = tfRef.current;
      const rect = el.getBoundingClientRect();
      // Cursor position relative to the stage center.
      const cx0 = e.clientX - (rect.left + rect.width / 2);
      const cy0 = e.clientY - (rect.top + rect.height / 2);
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const scale = clamp(t.scale * factor, MIN_SCALE, MAX_SCALE);
      const ratio = scale / t.scale;
      // Keep the point under the cursor stationary while zooming.
      const tx = cx0 - (cx0 - t.tx) * ratio;
      const ty = cy0 - (cy0 - t.ty) * ratio;
      setTransform({ scale, tx, ty }, false);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open, setTransform]);

  // --- Pointer gestures: pan (1 pointer) + pinch-zoom (2) -------------------
  const midAndDist = () => {
    const pts = [...pointersRef.current.values()];
    const [a, b] = pts;
    return {
      mx: (a.x + b.x) / 2,
      my: (a.y + b.y) / 2,
      dist: Math.hypot(a.x - b.x, a.y - b.y),
    };
  };

  const onPointerDown = (e) => {
    // Only react to primary-button presses / touches / pen.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2) {
      const { dist } = midAndDist();
      pinchRef.current = { startDist: dist, startScale: tfRef.current.scale };
    }
  };

  const onPointerMove = (e) => {
    const p = pointersRef.current.get(e.pointerId);
    if (!p) return;
    const prevX = p.x;
    const prevY = p.y;
    p.x = e.clientX;
    p.y = e.clientY;

    const count = pointersRef.current.size;
    if (count === 1) {
      // Pan. (At fit scale setTransform snaps back to center, so panning a
      // non-zoomed image is a no-op — which feels right.)
      const t = tfRef.current;
      setTransform({ tx: t.tx + (e.clientX - prevX), ty: t.ty + (e.clientY - prevY) }, false);
    } else if (count === 2 && pinchRef.current) {
      const { dist } = midAndDist();
      const { startDist, startScale } = pinchRef.current;
      if (startDist > 0) {
        const scale = clamp((dist / startDist) * startScale, MIN_SCALE, MAX_SCALE);
        setTransform({ scale }, false);
      }
    }
  };

  const endPointer = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
  };

  // Double-tap (touch) / double-click (desktop) toggles zoom.
  const toggleZoom = () => {
    const atFit = tfRef.current.scale <= MIN_SCALE + 0.001;
    setTransform({ scale: atFit ? DOUBLE_TAP_SCALE : MIN_SCALE, tx: 0, ty: 0 }, true);
  };

  const onPointerUp = (e) => {
    // Touch double-tap detection (mouse uses onDoubleClick below).
    if (e.pointerType === 'touch' && pointersRef.current.size === 1) {
      const now = e.timeStamp;
      const last = lastTapRef.current;
      const near = Math.hypot(e.clientX - last.x, e.clientY - last.y) < 30;
      if (now - last.t < 300 && near) {
        toggleZoom();
        lastTapRef.current = { t: 0, x: 0, y: 0 };
      } else {
        lastTapRef.current = { t: now, x: e.clientX, y: e.clientY };
      }
    }
    endPointer(e);
  };

  // Toolbar zoom buttons zoom around the image center.
  const zoomBy = (factor) => {
    const t = tfRef.current;
    const scale = clamp(t.scale * factor, MIN_SCALE, MAX_SCALE);
    const ratio = scale / t.scale;
    setTransform({ scale, tx: t.tx * ratio, ty: t.ty * ratio }, true);
  };

  if (!open || !src) return null;

  const { scale, tx, ty, rotate } = view;
  const transform = `translate(${tx}px, ${ty}px) scale(${scale}) rotate(${rotate}deg)`;

  const node = (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      // Backdrop click closes — but only when the click lands on the overlay/
      // stage itself, not on a toolbar button (those stopPropagation).
      onClick={onClose}
    >
      <div
        ref={stageRef}
        className={classes.stage}
        onClick={(e) => e.stopPropagation() /* clicks on the image shouldn't close */}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={endPointer}
        onDoubleClick={toggleZoom}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={cx(classes.image, animated && classes.animated)}
          style={{ transform }}
          draggable={false}
        />
      </div>

      <div className={classes.toolbar} onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              className={classes.button}
              size="small"
              onClick={() => zoomBy(1 / 1.4)}
              disabled={scale <= MIN_SCALE + 0.001}
              aria-label="Zoom out"
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              className={classes.button}
              size="small"
              onClick={() => zoomBy(1.4)}
              disabled={scale >= MAX_SCALE - 0.001}
              aria-label="Zoom in"
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rotate">
          <IconButton
            className={classes.button}
            size="small"
            onClick={() => setTransform({ rotate: tfRef.current.rotate + 90 }, true)}
            aria-label="Rotate 90 degrees"
          >
            <RotateRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset">
          <IconButton
            className={classes.button}
            size="small"
            onClick={reset}
            aria-label="Reset view"
          >
            <ResetIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton
            ref={closeBtnRef}
            className={classes.button}
            size="small"
            onClick={onClose}
            aria-label="Close photo viewer"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default PhotoViewer;
